import { useAtomValue, useSetAtom } from 'jotai';
import { useState } from 'react';

import {
	ActionItemStatus,
	ROBOTIC_SESSION_TYPES,
	ROBOTIC_SIGNING_STATUS,
} from '@ekhodealer/ekho-common';
import { Button, Checkbox, Divider,Group, Stack, Text, toast } from '@ekhodealer/ekho-common/rev';

import type { ActionItem, CounterSignFormInfo,RoboticSessionType } from '@ekhodealer/ekho-common';

import Download24 from '@common/components/icons/Download24';

import { useApproveCounterSignRobosignTarPaperwork } from '../../../api/progress/useApproveCounterSignRobosignTarPaperwork';
import { useDownloadVendorForm } from '../../../api/progress/useDownloadVendorForm';
import { useDownloadVosPacket } from '../../../api/progress/useDownloadVosPacket';
import { useGetApproveCounterSignRobosignTarPaperwork } from '../../../api/progress/useGetApproveCounterSignRobosignTarPaperwork';
import { useGetApproveRobosignTarPaperwork } from '../../../api/progress/useGetApproveRobosignTarPaperwork';
import { useUpdateRobosignStatus } from '../../../api/progress/useUpdateRobosignStatus';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

interface ApproveRobosignDetailProps {
	actionItem: ActionItem;
}

/**
 * Component to display buyer information
 */
const BuyerInfoSection = ({
	buyerInfo,
}: {
	buyerInfo: { fullName: string; email: string; phone: string; addressString: string };
}) => (
	<Stack itemsSpacing='8'>
		<Text weight='bold' size='bodySmall'>
			Buyer Information
		</Text>
		<Stack itemsSpacing='4'>
			<Group itemsSpacing='8' itemsAlignY='center'>
				<Text size='bodySmall' color='secondary'>
					Name:
				</Text>
				<Text size='bodySmall'>{buyerInfo.fullName || 'N/A'}</Text>
			</Group>
			<Group itemsSpacing='8' itemsAlignY='center'>
				<Text size='bodySmall' color='secondary'>
					Email:
				</Text>
				<Text size='bodySmall'>{buyerInfo.email || 'N/A'}</Text>
			</Group>
			<Group itemsSpacing='8' itemsAlignY='center'>
				<Text size='bodySmall' color='secondary'>
					Phone:
				</Text>
				<Text size='bodySmall'>{buyerInfo.phone || 'N/A'}</Text>
			</Group>
			<Group itemsSpacing='8' itemsAlignY='start'>
				<Text size='bodySmall' color='secondary'>
					Address:
				</Text>
				<Text size='bodySmall'>{buyerInfo.addressString || 'N/A'}</Text>
			</Group>
		</Stack>
	</Stack>
);

/**
 * Component to display dealer information
 */
const DealerInfoSection = ({
	sellerInfo,
}: {
	sellerInfo: {
		agentName: string;
		agentEmail: string;
		agentPhone: string;
		dealershipName: string;
		addressString: string;
	};
}) => (
	<Stack itemsSpacing='8'>
		<Text weight='bold' size='bodySmall'>
			Dealer Information
		</Text>
		<Stack itemsSpacing='4'>
			<Group itemsSpacing='8' itemsAlignY='center'>
				<Text size='bodySmall' color='secondary'>
					Dealership:
				</Text>
				<Text size='bodySmall'>{sellerInfo.dealershipName || 'N/A'}</Text>
			</Group>
			<Group itemsSpacing='8' itemsAlignY='center'>
				<Text size='bodySmall' color='secondary'>
					Agent Name:
				</Text>
				<Text size='bodySmall'>{sellerInfo.agentName || 'N/A'}</Text>
			</Group>
			<Group itemsSpacing='8' itemsAlignY='center'>
				<Text size='bodySmall' color='secondary'>
					Agent Email:
				</Text>
				<Text size='bodySmall'>{sellerInfo.agentEmail || 'N/A'}</Text>
			</Group>
			<Group itemsSpacing='8' itemsAlignY='center'>
				<Text size='bodySmall' color='secondary'>
					Phone:
				</Text>
				<Text size='bodySmall'>{sellerInfo.agentPhone || 'N/A'}</Text>
			</Group>
			<Group itemsSpacing='8' itemsAlignY='start'>
				<Text size='bodySmall' color='secondary'>
					Address:
				</Text>
				<Text size='bodySmall'>{sellerInfo.addressString || 'N/A'}</Text>
			</Group>
		</Stack>
	</Stack>
);

/**
 * Component to display forms list with optional download buttons
 */
const FormsListSection = ({
	title,
	forms,
	showDownload = false,
	onDownload,
	isDownloading = false,
}: {
	title: string;
	forms: CounterSignFormInfo[];
	showDownload?: boolean;
	onDownload?: (form: CounterSignFormInfo) => void;
	isDownloading?: boolean;
}) => {
	if (forms.length === 0) return null;

	return (
		<Stack itemsSpacing='8'>
			<Text weight='bold' size='bodySmall'>
				{title}
			</Text>
			<Stack itemsSpacing='4'>
				{forms.map((form) => (
					<Group
						key={form.key}
						itemsSpacing='8'
						itemsAlignY='center'
						itemsAlignX='space-between'>
						<Text size='bodySmall'>{form.name || form.key}</Text>
						{showDownload && onDownload ? (
							<Button
								variant='ghost'
								size='icon'
								onPress={() => onDownload(form)}
								aria-label={`Download ${form.name || form.key}`}
								isLoading={isDownloading}>
								<Download24 />
							</Button>
						) : (
							<Text size='bodySmall' color='secondary' isItalic>
								Not downloadable
							</Text>
						)}
					</Group>
				))}
			</Stack>
		</Stack>
	);
};

/**
 * ApproveRobosignDetail component for approving robotic signing sessions.
 * Handles both buyer sessions (APPROVE_ROBOSIGN_TAR_PAPERWORK) and
 * dealer signing sessions (APPROVE_COUNTER_SIGN_ROBOSIGN_TAR_PAPERWORK).
 *
 * Uses the sessionType from action item metadata to determine which session to approve.
 * Dealer signing sessions use the dedicated admin portal endpoint for proper tracking.
 */
export const ApproveRobosignDetail = ({ actionItem }: ApproveRobosignDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	// Hook for buyer sessions
	const updateStatusMutation = useUpdateRobosignStatus(orderId);
	// Hook for counter-signing sessions
	const approveCounterSignMutation = useApproveCounterSignRobosignTarPaperwork(orderId);
	// Hook for downloading VOS packet
	const downloadVosPacketMutation = useDownloadVosPacket(orderId);
	// Hook for downloading vendor forms
	const downloadVendorFormMutation = useDownloadVendorForm();

	const [isApproved, setIsApproved] = useState(false);

	// Get session type from action item metadata, default to BUYER for backwards compatibility
	const sessionType: RoboticSessionType =
		(actionItem.metadata?.sessionType as RoboticSessionType) || ROBOTIC_SESSION_TYPES.BUYER;

	const isComplete = actionItem.status === ActionItemStatus.COMPLETE;
	const isDealerSigning = sessionType === ROBOTIC_SESSION_TYPES.COUNTER_SIGNING;

	// Fetch data based on session type
	const buyerSessionQuery = useGetApproveRobosignTarPaperwork(
		!isDealerSigning ? orderId : undefined,
	);
	const dealerSignSessionQuery = useGetApproveCounterSignRobosignTarPaperwork(
		isDealerSigning ? orderId : undefined,
	);

	// Get the appropriate data based on session type
	const buyerInfo = isDealerSigning
		? dealerSignSessionQuery.data?.buyerInfo
		: buyerSessionQuery.data?.buyerInfo;

	const sellerInfo = isDealerSigning ? dealerSignSessionQuery.data?.sellerInfo : undefined;

	const hasVosPacket = !isDealerSigning && buyerSessionQuery.data?.hasVosPacket;
	const dealerSignForms = isDealerSigning
		? dealerSignSessionQuery.data?.counterSignForms || []
		: [];
	const poaForms = isDealerSigning ? dealerSignSessionQuery.data?.poaForms || [] : [];

	// Use the appropriate mutation based on session type
	const isPending = isDealerSigning
		? approveCounterSignMutation.isPending
		: updateStatusMutation.isPending;

	const isLoadingData = isDealerSigning
		? dealerSignSessionQuery.isLoading
		: buyerSessionQuery.isLoading;

	const handleApprove = async () => {
		if (!isApproved) {
			toast({
				message: 'Please confirm by checking the checkbox before approving',
				variant: 'warning',
			});
			return;
		}

		try {
			if (isDealerSigning) {
				// Use dedicated admin portal endpoint for dealer signing sessions
				// This sets hasBeenCountersigned=true and properly tracks completion
				await approveCounterSignMutation.mutateAsync();
			} else {
				// Use original endpoint for buyer sessions
				await updateStatusMutation.mutateAsync({
					status: ROBOTIC_SIGNING_STATUS.approved,
					sessionType,
				});
			}

			toast({
				message: `Robotic signing session approved successfully`,
				variant: 'success',
			});

			// Navigate back to progress view
			navigateBack();
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : 'Failed to approve robotic signing session';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	};

	const handleDownloadVosPacket = () => {
		downloadVosPacketMutation.mutate();
	};

	const handleDownloadPoaForm = (form: CounterSignFormInfo) => {
		if (!orderId) return;
		downloadVendorFormMutation.mutate({
			orderId,
			formKey: form.key,
			formName: form.name,
		});
	};

	// Copy variants based on session type
	const copy = isDealerSigning
		? {
				completedMessage:
					'The dealer robotic signing session has been approved. The signed TAR documents are now complete.',
				instructions:
					'Review the dealer robotic signing session. This is where the selling dealer signs the TAR documents after the buyer has already completed their robotic signing session.',
				sessionTypeLabel: 'Dealer Signing Session',
				sessionTypeDescription:
					'This is a dealer robotic signing session. Forms that cannot be downloaded have already been signed by the buyer and now need to be signed by the dealer. Forms that can be downloaded need to be printed and signed by you, then signed by the dealer.',
				checkboxLabel:
					'I confirm that I have completed the dealer robotic signing session and the signed documents are correct.',
				buttonLabel: actionItem.config.buttonTitle || 'Approve Dealer Robotic Session',
		  }
		: {
				completedMessage:
					'The buyer robotic signing session has been approved. The buyer-signed TAR documents are complete.',
				instructions:
					'Review the buyer robotic signing session. This is where the buyer signs the TAR documents as part of the initial signing process.',
				sessionTypeLabel: 'Buyer Signing',
				sessionTypeDescription:
					'This is a buyer robotic signing session. Download the VOS packet, then have the buyer sign the forms where relevant.',
				checkboxLabel:
					'I confirm that I have completed the buyer robotic signing session and the signed documents are correct.',
				buttonLabel: 'Approve Buyer Session',
		  };

	if (isComplete) {
		return (
			<Stack itemsSpacing='16'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{copy.completedMessage}
				</Text>
			</Stack>
		);
	}

	return (
		<Stack isFullHeight itemsAlignY='space-between'>
			<Stack itemsSpacing='16'>
				{/* Session Type Info */}
				<Stack itemsSpacing='8'>
					<Text weight='bold'>Session Type</Text>
					<Text>{copy.sessionTypeLabel}</Text>
					<Text color='secondary'>{copy.sessionTypeDescription}</Text>
				</Stack>

				<Divider />

				{/* Buyer Information */}
				{isLoadingData ? (
					<Text size='bodySmall' color='secondary'>
						Loading buyer information...
					</Text>
				) : buyerInfo ? (
					<BuyerInfoSection buyerInfo={buyerInfo} />
				) : null}

				{/* Dealer Information (Dealer Signing Session Only) */}
				{isDealerSigning && !isLoadingData && sellerInfo && (
					<>
						<Divider />
						<DealerInfoSection sellerInfo={sellerInfo} />
					</>
				)}

				{/* VOS Packet Download (Buyer Session Only) */}
				{!isDealerSigning && hasVosPacket && (
					<>
						<Divider />
						<Group itemsSpacing='8' itemsAlignY='start' itemsAlignX='space-between'>
							<Stack itemsSpacing='4'>
								<Text weight='bold' size='bodySmall'>
									VOS Packet
								</Text>
								<Text size='bodySmall' color='secondary'>
									Download the VOS packet containing forms that need to be
									robotically signed.
								</Text>
							</Stack>
							<Button
								variant='ghost'
								size='icon'
								onPress={handleDownloadVosPacket}
								aria-label='Download VOS Packet'
								isLoading={downloadVosPacketMutation.isPending}>
								<Download24 />
							</Button>
						</Group>
					</>
				)}

				{/* Dealer Signing Forms List (Dealer Signing Session Only) */}
				{isDealerSigning && dealerSignForms.length > 0 && (
					<>
						<Divider />
						<FormsListSection
							title='Forms for Dealer to Sign'
							forms={dealerSignForms}
							showDownload={false}
						/>
					</>
				)}

				{/* POA Forms List with Download (Dealer Signing Session Only) */}
				{isDealerSigning && poaForms.length > 0 && (
					<>
						<Divider />
						<FormsListSection
							title='POA-signed forms'
							forms={poaForms}
							showDownload={true}
							onDownload={handleDownloadPoaForm}
							isDownloading={downloadVendorFormMutation.isPending}
						/>
					</>
				)}

				<Divider />

				{/* Approval Checkbox */}
				<Checkbox
					isSelected={isApproved}
					onChange={setIsApproved}
					isDisabled={isPending}
					label={copy.checkboxLabel}
				/>
			</Stack>

			{/* Action Buttons */}
			<Group itemsSpacing='8' noWrap>
				<Group.Item grow={1}>
					<Button
						variant='secondary'
						size='large'
						isFullWidth
						onPress={navigateBack}
						isDisabled={isPending}>
						Cancel
					</Button>
				</Group.Item>
				<Group.Item grow={1}>
					<Button
						size='large'
						isFullWidth
						onPress={handleApprove}
						isLoading={isPending}
						isDisabled={!isApproved}>
						Confirm
					</Button>
				</Group.Item>
			</Group>
		</Stack>
	);
};
